// Modal "Testar mensagem de aniversário" — mockup "Modal Testar Mensagem"
// (520px). Substitui o `prompt("Digite o ID de um contato")` que existia aqui:
// a pessoa busca o contato pelo nome, vê o que será enviado e é avisada quando
// o contato não tem data de nascimento (a variável {idade} sai vazia).
import React, { useEffect, useState } from "react";

import { makeStyles, alpha } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import SendIcon from "@material-ui/icons/Send";
import WarningIcon from "@material-ui/icons/Warning";
import CircularProgress from "@material-ui/core/CircularProgress";

import AerosDialog from "../AerosDialog";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import { AEROS, stateText } from "../../styles/aerosTokens";
import {
  resolveMessage,
  missingVariables,
  ageFromBirthDate,
  formatBirthDate,
  initials,
  avatarColor,
} from "../../pages/BirthdaySettings/birthdayRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const fieldBg = isLight ? theme.palette.aeros.surface2 : "#12151F";
  return {
    label: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      marginBottom: 6,
    },
    input: {
      "& .MuiOutlinedInput-root": {
        backgroundColor: fieldBg,
        borderRadius: 10,
        fontSize: 13.5,
        "& fieldset": {
          borderColor: isLight
            ? theme.palette.aeros.borderStrong
            : "rgba(255,255,255,0.1)",
        },
        "&:hover fieldset": { borderColor: alpha(AEROS.cyan, 0.5) },
        "&.Mui-focused fieldset": { borderColor: AEROS.cyan, borderWidth: 1 },
        "&.Mui-focused": { boxShadow: `0 0 0 3px ${alpha(AEROS.cyan, 0.15)}` },
      },
      "& .MuiOutlinedInput-input": { padding: "9px 12px" },
      "& .MuiInputAdornment-root .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.aeros.textTertiary,
      },
    },
    results: {
      backgroundColor: fieldBg,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.1)"}`,
      borderRadius: 10,
      marginTop: 6,
      overflow: "hidden",
      boxShadow: "0 12px 30px rgba(0,0,0,0.5)",
      maxHeight: 210,
      overflowY: "auto",
      ...theme.scrollbarStylesSoft,
    },
    result: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "9px 12px",
      width: "100%",
      border: "none",
      background: "transparent",
      cursor: "pointer",
      textAlign: "left",
      fontFamily: "inherit",
      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
      },
      "&:focus-visible": { outline: `2px solid ${AEROS.cyan}`, outlineOffset: -2 },
    },
    resultActive: { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
    avatar: {
      width: 30,
      height: 30,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      fontWeight: 700,
      color: "#07080D",
      flex: "none",
    },
    resultName: { fontSize: 13, fontWeight: 600, color: theme.palette.text.primary },
    resultMeta: { fontSize: 11, color: theme.palette.text.secondary },
    resultBirth: { fontSize: 11, color: theme.palette.text.secondary, flex: "none" },
    resultNoBirth: {
      fontSize: 11,
      color: stateText(theme, AEROS.warning, "warning"),
      flex: "none",
    },
    empty: {
      fontSize: 12,
      color: theme.palette.aeros.textTertiary,
      padding: "12px",
      textAlign: "center",
    },
    previewCard: {
      backgroundColor: fieldBg,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"}`,
      borderRadius: 12,
      padding: "12px 14px",
      fontSize: 12.5,
      lineHeight: 1.55,
      color: theme.palette.text.primary,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    },
    previewEyebrow: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      marginBottom: 6,
    },
    warning: {
      fontSize: 11,
      color: stateText(theme, AEROS.warning, "warning"),
      marginTop: 6,
      display: "flex",
      alignItems: "flex-start",
      gap: 4,
      lineHeight: 1.45,
      "& .MuiSvgIcon-root": { fontSize: 13, marginTop: 1, flex: "none" },
    },
  };
});

const TestMessageModal = ({ open, onClose, message, connectionName }) => {
  const classes = useStyles();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [sending, setSending] = useState(false);

  // Reabrir o modal precisa começar limpo: manter o contato da rodada
  // anterior faria "Enviar teste" disparar para quem a pessoa não escolheu.
  useEffect(() => {
    if (!open) return;
    setSearch("");
    setResults([]);
    setSelected(null);
    setSending(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    // Já escolheu alguém: a lista some e a busca para de rodar.
    if (selected) return undefined;
    const termo = search.trim();
    if (termo.length < 2) {
      setResults([]);
      return undefined;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", {
          params: { searchParam: termo, pageNumber: 1 },
        });
        setResults((data?.contacts || []).slice(0, 6));
      } catch (err) {
        toastError(err);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [search, open, selected]);

  const idade = selected ? ageFromBirthDate(selected.birthDate) : null;
  const preview = selected
    ? resolveMessage(message, { name: selected.name, age: idade })
    : "";
  const faltando = selected
    ? missingVariables(message, { name: selected.name, age: idade })
    : [];

  const handleSend = async () => {
    if (!selected) return;
    setSending(true);
    try {
      await api.post("/birthdays/test-message", {
        contactId: selected.id,
        messageType: "contact",
      });
      toast.success(i18n.t("aeros.birthday.test.sent"));
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <AerosDialog
      open={open}
      onClose={onClose}
      width={520}
      title={i18n.t("aeros.birthday.test.title")}
      subtitle={
        connectionName
          ? i18n.t("aeros.birthday.test.subtitle", { connection: connectionName })
          : i18n.t("aeros.birthday.test.subtitleDefault")
      }
      onSubmit={handleSend}
      submitLabel={i18n.t("aeros.birthday.test.send")}
      submitIcon={<SendIcon />}
      submitDisabled={!selected}
      loading={sending}
    >
      <div>
        <div className={classes.label}>{i18n.t("aeros.birthday.test.contact")}</div>
        <TextField
          value={selected ? selected.name : search}
          onChange={(e) => {
            // Digitar depois de escolher volta para a busca — senão o campo
            // ficaria travado no contato selecionado.
            if (selected) setSelected(null);
            setSearch(e.target.value);
          }}
          placeholder={i18n.t("aeros.birthday.test.searchPlaceholder")}
          variant="outlined"
          fullWidth
          autoFocus
          className={classes.input}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {searching ? <CircularProgress size={16} /> : <SearchIcon />}
              </InputAdornment>
            ),
          }}
          inputProps={{ "aria-label": i18n.t("aeros.birthday.test.contact") }}
        />
        {!selected && search.trim().length >= 2 && (
          <div className={classes.results}>
            {results.length === 0 ? (
              <div className={classes.empty}>
                {i18n.t(
                  searching
                    ? "aeros.birthday.test.searching"
                    : "aeros.birthday.test.noResults"
                )}
              </div>
            ) : (
              results.map((contact) => (
                <button
                  type="button"
                  key={contact.id}
                  className={classes.result}
                  onClick={() => setSelected(contact)}
                >
                  <span
                    className={classes.avatar}
                    style={{ backgroundColor: avatarColor(contact.id) }}
                  >
                    {initials(contact.name)}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span className={classes.resultName} style={{ display: "block" }}>
                      {contact.name}
                    </span>
                    <span className={classes.resultMeta} style={{ display: "block" }}>
                      {contact.number} ·{" "}
                      {i18n.t("aeros.birthday.test.idLabel", { id: contact.id })}
                    </span>
                  </span>
                  {contact.birthDate ? (
                    <span className={classes.resultBirth}>
                      {formatBirthDate(contact.birthDate)}
                    </span>
                  ) : (
                    <span className={classes.resultNoBirth}>
                      {i18n.t("aeros.birthday.test.noBirth")}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {selected && (
        <div className={classes.previewCard}>
          <div className={classes.previewEyebrow}>
            {i18n.t("aeros.birthday.test.willSend")}
          </div>
          {preview}
          {faltando.length > 0 && (
            <div className={classes.warning}>
              <WarningIcon />
              <span>
                {i18n.t("aeros.birthday.test.missingWarning", {
                  vars: faltando.join(", "),
                })}
              </span>
            </div>
          )}
        </div>
      )}
    </AerosDialog>
  );
};

export default TestMessageModal;
